import { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

import { ProjectVisibility } from "@optidev/shared-types";
import { VisibilitySelector } from "./VisibilitySelector";

interface PublishVisibilityModalProps {
  isOpen: boolean;
  currentVisibility?: ProjectVisibility;
  onClose: () => void;
  onPublish: (visibility: ProjectVisibility) => void;
  isUpdating?: boolean;
}

export function PublishVisibilityModal({
  isOpen,
  currentVisibility,
  onClose,
  onPublish,
  isUpdating = false,
}: PublishVisibilityModalProps) {
  const [selectedVisibility, setSelectedVisibility] =
    useState<ProjectVisibility>(currentVisibility || ProjectVisibility.PUBLIC);

  // Sync with store when modal opens or currentVisibility changes
  useEffect(() => {
    if (currentVisibility) {
      setSelectedVisibility(currentVisibility);
    }
  }, [currentVisibility]);

  const handlePublish = () => {
    onPublish(selectedVisibility);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[360px] w-full max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Publish Project</DialogTitle>
          <DialogDescription>
            Choose who can access your published content
          </DialogDescription>
        </DialogHeader>

        <div className="py-1">
          <VisibilitySelector
            value={selectedVisibility}
            onChange={setSelectedVisibility}
            disabled={isUpdating}
            isOpen={isOpen}
            onClose={onClose}
          />
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button size="sm" onClick={handlePublish} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Publishing...
              </>
            ) : (
              "Publish"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
